import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useCompare } from "@/context/CompareContext";
import api from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Scale, ArrowRight } from "lucide-react";

const FUELS = ["essence", "diesel", "hybride", "electrique"];
const TRANS = ["automatique", "manuelle"];

const TABS = [
  {
    id: "all", label: "Nos véhicules", params: { exclude_sold: true },
    cover: "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/4857124966.jpg",
    intro: "Notre catalogue disponible : véhicules en stock, youngtimers et oldtimers. Chaque voiture a été rigoureusement inspectée, son historique documenté et son origine certifiée.",
  },
  {
    id: "stock", label: "En stock", params: { status: "available" },
    cover: "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/4857124966.jpg",
    intro: "Notre sélection actuelle de véhicules disponibles à la vente. Réception uniquement sur rendez-vous au showroom de Rhode-Saint-Genèse.",
  },
  {
    id: "sold", label: "Vendus récemment", params: { status: "sold" },
    cover: "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/4857140257.jpg",
    intro: "La preuve qu'on trouve preneur vite, au juste prix. Voici quelques-unes des belles autos qui sont passées par nos mains — elles font désormais le bonheur de leurs nouveaux propriétaires. Un modèle vous intéresse ? Contactez-nous pour être prévenu du prochain arrivage similaire.",
  },
  {
    id: "youngtimer", label: "Youngtimers", params: { category: "youngtimer" },
    cover: "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/4857143616.jpg",
    intro: "Véhicules de collection dont l'âge se situe entre 15 et 30 ans. Ils ont connu leur heure de gloire à la fin des années 70, durant les années 80 ou encore au début des années 90. Aujourd'hui, si ces véhicules sont considérés comme des ancêtres, avec 25 ans d'âge au compteur, ils appartiennent toutefois à une catégorie celle que l'on surnomme les « youngtimers » et sa signification peut être interprétée comme « jeune-ancienne ».",
  },
  {
    id: "oldtimer", label: "Oldtimers", params: { category: "oldtimer" },
    cover: "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/4857131128.jpg",
    intro: "Véhicules d'exception de plus de 30 ans qui ont marqué l'histoire de l'automobile. Sportives mythiques, GT rares, cabriolets emblématiques — chaque oldtimer que nous proposons a été soigneusement sélectionné pour son authenticité, son état de conservation et son intérêt collection.",
  },
];

export default function Stock() {
  const { t } = useLang();
  const { ids: compareIds, remove: removeCompare, clear: clearCompare } = useCompare();
  const [tab, setTab] = useState("all");
  const [smartFilter, setSmartFilter] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");
  const [f, setF] = useState({
    brand: "", min_price: "", max_price: "", min_year: "", max_year: "",
    max_mileage: "", fuel_type: "", transmission: "",
  });

  useEffect(() => {
    api.get("/vehicles/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const active = TABS.find((x) => x.id === tab);
    const params = { ...(active?.params || {}) };
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/vehicles", { params }).then((r) => setVehicles(r.data)).finally(() => setLoading(false));
  }, [f, tab]);

  const setField = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const reset = () => setF({ brand: "", min_price: "", max_price: "", min_year: "", max_year: "", max_mileage: "", fuel_type: "", transmission: "" });

  // Pré-chargement des véhicules sélectionnés au comparateur pour afficher leur nom sur la barre
  const [compareVehicles, setCompareVehicles] = useState([]);
  useEffect(() => {
    if (compareIds.length === 0) { setCompareVehicles([]); return; }
    Promise.all(compareIds.map((id) => api.get(`/vehicles/${id}`).then((r) => r.data).catch(() => null)))
      .then((arr) => setCompareVehicles(arr.filter(Boolean)));
  }, [compareIds]);

  const sorted = useMemo(() => {
    let arr = [...vehicles];
    if (sort === "price_asc") arr.sort((a, b) => (a.price || 0) - (b.price || 0));
    if (sort === "price_desc") arr.sort((a, b) => (b.price || 0) - (a.price || 0));
    if (sort === "year_desc") arr.sort((a, b) => (b.year || 0) - (a.year || 0));
    if (smartFilter && compareVehicles.length > 0) {
      const bodies = new Set(compareVehicles.map((v) => v.body_type).filter(Boolean));
      const categories = new Set(compareVehicles.map((v) => v.category).filter(Boolean));
      const brands = new Set(compareVehicles.map((v) => v.brand).filter(Boolean));
      arr = arr.filter((v) => {
        if (compareIds.includes(v.id)) return true;
        const bodyMatch = bodies.size === 0 || bodies.has(v.body_type);
        const catMatch = categories.size === 0 || categories.has(v.category);
        const brandMatch = brands.size === 0 || brands.has(v.brand);
        const score = (bodyMatch ? 1 : 0) + (catMatch ? 1 : 0) + (brandMatch ? 1 : 0);
        return score >= 2;
      });
    }
    return arr;
  }, [vehicles, sort, smartFilter, compareVehicles, compareIds]);

  return (
    <div data-testid="stock-page" className="pt-32 pb-24 bg-[#0D0D0D] min-h-screen">
      <SEO title="Notre stock · Porsche de prestige & voitures d'exception en Belgique" description="Sélection rigoureuse de Porsche de prestige et voitures d'exception en Belgique : 911 modernes et classiques, GT3, Turbo, GT-R, Alpine, youngtimers. Origine certifiée, dépôt-vente et achat cash — KV International, Rhode-Saint-Genèse." />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">01 — Inventory</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light text-white">{t.stock.title}</h1>
          <p className="text-white/60 text-lg mt-4 max-w-2xl">{t.stock.subtitle}</p>
        </div>

        {/* Rubriques / tabs */}
        <div data-testid="stock-tabs" className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-1">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              data-testid={`tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`px-5 py-3 text-xs uppercase tracking-[0.25em] transition-colors border-b-2 -mb-[1px] ${
                tab === tb.id
                  ? "text-[#C8A75A] border-[#C8A75A]"
                  : "text-white/50 border-transparent hover:text-white"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {/* Category presentation banner (image + text from kvinternational.be) */}
        {(() => {
          const active = TABS.find((x) => x.id === tab);
          if (!active?.intro) return null;
          return (
            <div data-testid={`tab-intro-${tab}`} className="grid md:grid-cols-[380px_1fr] gap-8 items-center mb-12 bg-[#1E1E1E]/60 border border-white/10 rounded-md p-6 md:p-8">
              <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden rounded-md">
                <img src={active.cover} alt={active.label} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#C8A75A] mb-3">{active.label}</p>
                <h2 className="font-display text-2xl md:text-3xl text-white mb-4 leading-[1.15]">
                  {tab === "youngtimer" ? "Youngtimers" : tab === "oldtimer" ? "Oldtimers" : tab === "sold" ? "Vendus récemment" : tab === "all" ? "Nos véhicules" : "Nos véhicules en stock"}
                </h2>
                {tab === "sold" && (
                  <p className="text-[#C8A75A] text-sm italic mb-2">La preuve qu'on trouve preneur vite, au juste prix.</p>
                )}
                <p className="text-white/70 text-base leading-relaxed">{active.intro}</p>
              </div>
            </div>
          );
        })()}

        <div className="grid lg:grid-cols-[300px_1fr] gap-10">
          {/* Filters */}
          <aside data-testid="filters-panel" className="bg-[#1E1E1E] border border-white/10 rounded-md p-6 h-fit lg:sticky lg:top-28">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl text-white">{t.stock.filters}</h3>
              <button data-testid="filters-reset" onClick={reset} className="text-xs uppercase tracking-widest text-[#C8A75A] hover:text-white flex items-center gap-1">
                <X className="w-3 h-3" />{t.stock.reset}
              </button>
            </div>

            <div className="space-y-5 text-sm">
              <Selector label={t.stock.brand} value={f.brand} onChange={(v) => setField("brand", v)} options={brands} allLabel={t.stock.all} testid="filter-brand" />
              <NumberInput label={t.stock.price_min} value={f.min_price} onChange={(v) => setField("min_price", v)} testid="filter-min-price" />
              <NumberInput label={t.stock.price_max} value={f.max_price} onChange={(v) => setField("max_price", v)} testid="filter-max-price" />
              <NumberInput label={t.stock.year_min} value={f.min_year} onChange={(v) => setField("min_year", v)} testid="filter-min-year" />
              <NumberInput label={t.stock.year_max} value={f.max_year} onChange={(v) => setField("max_year", v)} testid="filter-max-year" />
              <NumberInput label={t.stock.mileage_max} value={f.max_mileage} onChange={(v) => setField("max_mileage", v)} testid="filter-max-km" />
              <Selector label={t.stock.fuel} value={f.fuel_type} onChange={(v) => setField("fuel_type", v)} options={FUELS} allLabel={t.stock.all} testid="filter-fuel" />
              <Selector label={t.stock.transmission} value={f.transmission} onChange={(v) => setField("transmission", v)} options={TRANS} allLabel={t.stock.all} testid="filter-trans" />
            </div>
          </aside>

          {/* Results */}
          <div>
            <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/10 flex-wrap gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <p data-testid="results-count" className="text-white/60 text-sm">{t.stock.results(sorted.length)}</p>
                {compareIds.length >= 1 && (
                  <label className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] cursor-pointer select-none" data-testid="smart-filter-toggle">
                    <input type="checkbox" checked={smartFilter} onChange={(e) => setSmartFilter(e.target.checked)} className="accent-[#C8A75A] w-3.5 h-3.5" />
                    <span className={smartFilter ? "text-[#C8A75A]" : "text-white/50 hover:text-white"}>Suggérer les comparables uniquement</span>
                  </label>
                )}
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select" className="bg-[#1E1E1E] border-white/10 text-white text-sm w-[200px]">
                  <SelectValue placeholder={t.stock.sort} />
                </SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                  <SelectItem value="recent">{t.stock.sort_recent}</SelectItem>
                  <SelectItem value="price_asc">{t.stock.sort_price_asc}</SelectItem>
                  <SelectItem value="price_desc">{t.stock.sort_price_desc}</SelectItem>
                  <SelectItem value="year_desc">{t.stock.sort_year_desc}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-[4/3] bg-[#1E1E1E] rounded-md animate-pulse" />
                ))}
              </div>
            ) : sorted.length === 0 ? (
              <p className="text-white/40 py-32 text-center">{t.stock.no_results}</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
                {sorted.map((v) => <VehicleCard key={v.id} vehicle={v} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Barre flottante comparateur */}
      {compareIds.length > 0 && (
        <div data-testid="compare-bar" className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#C8A75A]/30 bg-[#0D0D0D]/95 backdrop-blur-md shadow-[0_-20px_60px_rgba(0,0,0,0.6)]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-[#C8A75A]">
              <Scale className="w-5 h-5" />
              <span className="text-xs uppercase tracking-[0.25em] font-medium">Comparateur · {compareIds.length}/4</span>
            </div>
            <div className="flex-1 flex items-center gap-2 flex-wrap min-w-[200px]">
              {compareVehicles.map((v) => (
                <span key={v.id} data-testid={`compare-chip-${v.id}`} className="flex items-center gap-2 bg-[#1E1E1E] border border-white/10 rounded-full pl-3 pr-1 py-1 text-xs text-white/80">
                  {v.brand} {v.model}
                  <button onClick={() => removeCompare(v.id)} className="w-5 h-5 rounded-full bg-white/5 hover:bg-red-400/20 text-white/50 hover:text-red-400 flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <button data-testid="compare-bar-clear" onClick={clearCompare} className="text-xs uppercase tracking-widest text-white/50 hover:text-red-400 px-3 py-2">Vider</button>
            <Link
              to="/compare"
              data-testid="compare-bar-cta"
              className={`flex items-center gap-2 px-6 py-3 rounded-md text-xs uppercase tracking-widest font-semibold transition-colors ${
                compareIds.length < 2
                  ? "bg-white/5 text-white/40 pointer-events-none"
                  : "bg-[#C8A75A] text-black hover:bg-white"
              }`}
            >
              Comparer <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Selector({ label, value, onChange, options, allLabel, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">{label}</label>
      <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger data-testid={testid} className="bg-[#0D0D0D] border-white/10 text-white text-sm">
          <SelectValue placeholder={allLabel} />
        </SelectTrigger>
        <SelectContent className="bg-[#1E1E1E] border-white/10 text-white max-h-[280px]">
          <SelectItem value="__all__">{allLabel}</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function NumberInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">{label}</label>
      <Input
        data-testid={testid}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#0D0D0D] border-white/10 text-white text-sm h-10"
      />
    </div>
  );
}
